import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Checkbox, Dropdown, Preloader, SearchInput, Text } from '@common-fe/common-fe';
import _ from 'lodash';

import { OrganizationTypes } from '@/common/constants';
import { useCurrentOrganization } from '@/modules/core';

import { useEmployersDropdownListQuery } from './queries';
interface Props {
  isAutoFocus?: boolean;
  onSetAutoFocus?: (value: boolean) => void;
  values?: string[];
  onChangeValues?: (value: string[]) => void;
  testId?: string;
  disabled?: boolean;
}
const MIN_CHARS = 1;

const DEFAULT_DROP_SIZE = 250;
// Fixed baseline so the virtualized scroll container has a real height and shows
// items. It must NOT grow with options.length — a growing min keeps the last row
// permanently visible and makes InfiniteLoading recursively fetch every page.
const MIN_DROP_SIZE = 120;
const MultipleEmployerSelector: React.FC<Props> = ({
  testId = 'multiple-employer-selector',
  isAutoFocus,
  values = [],
  onChangeValues,
  onSetAutoFocus,
  disabled,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { observingOrganization } = useCurrentOrganization();
  const isSubsidiary = useMemo(() => {
    return observingOrganization?.type === OrganizationTypes.subsidiary;
  }, [observingOrganization]);

  const [searchString, setSearchString] = useState('');

  const { options, isLoading, handleLoadMore, title, isInputLoading } = useEmployersDropdownListQuery({
    searchString: _.trim(searchString || '').length >= MIN_CHARS ? searchString : '',
    defaultEmployerIds: values,
  });

  const handlePickItem = useCallback(
    (key: string) => () => {
      if (onChangeValues) {
        if (values.includes(key)) {
          const filtered = values.filter((val) => val !== key);
          onChangeValues(filtered);
        } else {
          onChangeValues([...values, key]);
        }
      }
    },
    [onChangeValues, values]
  );
  const handleClear = useCallback(() => {
    if (onChangeValues) {
      onChangeValues([]);
    }
  }, [onChangeValues]);

  const handleChangeSearchString = useCallback(
    (newValue: string) => {
      if (!disabled) {
        setSearchString(newValue);
      }
    },
    [disabled]
  );
  const nodes = useMemo(() => {
    const list: React.ReactNode[] = [
      <Box
        key="-1"
        pad={{ vertical: 'spacing2', horizontal: 'spacing8' }}
        data-testId={`${testId}_item_zero`}
        direction="row"
        align="center"
        overflow="hidden"
        height="32px"
      >
        <Checkbox checked={!values.length} onClick={handleClear} />
        <Text truncate onClick={handleClear} style={{ cursor: 'pointer' }}>
          All
        </Text>
      </Box>,
    ];
    options.forEach((item) => {
      list.push(
        <Box
          key={item.key}
          pad={{ vertical: 'spacing4', horizontal: 'spacing8' }}
          data-testId={`${testId}_item_${item.key}`}
          direction="row"
          overflow="hidden"
          align="center"
          height="32px"
        >
          <div>
            <Checkbox
              checked={values.includes(item.key as string)}
              onClick={handlePickItem(item.key as string)}
            />
          </div>

          <Text
            truncate
            title={item.title}
            onClick={handlePickItem(item.key as string)}
            style={{ cursor: 'pointer' }}
          >
            {item.title}
          </Text>
        </Box>
      );
    });
    return list;
  }, [handleClear, handlePickItem, options, testId, values]);

  const handleToggleExpand = useCallback(
    (visible: boolean) => {
      if (!disabled) {
        setExpanded(visible);
        if (!visible && !values) {
          setSearchString('');
        }
      }
    },
    [disabled, values]
  );

  const handleAutoFocusBlur = useCallback(() => {
    if (onSetAutoFocus) {
      onSetAutoFocus(false);
    }
  }, [onSetAutoFocus]);
  useEffect(() => {
    if (isAutoFocus) {
      setExpanded(true);
    }
  }, [isAutoFocus]);
  return (
    <Dropdown
      placeholder="Placeholder"
      expanded={expanded}
      disabled={isSubsidiary}
      testId={`${testId}_dropdown`}
      prefix="Employer"
      contentStyles={{
        style: {
          marginTop: '2px',
          zIndex: 1000000,
        },
      }}
      title={
        <>
          {isInputLoading ? (
            <Preloader size={14} thinSize={2} />
          ) : (
            <Text truncate title={title} color="textAccent">
              {title || 'All'}
            </Text>
          )}
        </>
      }
      onSetExpanded={handleToggleExpand}
    >
      <Box pad="spacing12" border={{ side: 'bottom', size: '1px', color: 'border1' }}>
        <SearchInput
          key={`${isAutoFocus}`}
          disabled={isSubsidiary || disabled}
          autoFocus={true}
          testId={`${testId}_search`}
          onFocus={() => {
            setExpanded(true);
          }}
          onBlur={handleAutoFocusBlur}
          placeholder="Search"
          value={searchString}
          onChange={disabled ? () => {} : handleChangeSearchString}
        />
      </Box>
      {nodes.length || isLoading ? (
        <Box pad="spacing8">
          <Dropdown.InfiniteLoading
            testId={`${testId}_loading`}
            onLoadMore={handleLoadMore}
            isLoadMoreAvailable
            isLoading={isLoading}
            itemHeight={32}
            nodes={nodes}
            wrapperBox={{
              height: {
                min: `${MIN_DROP_SIZE}px`,
                max: `${DEFAULT_DROP_SIZE}px`,
              },
            }}
          />
        </Box>
      ) : (
        <Box pad="spacing12">
          <Text data-testid={`${testId}_not-found`} color="textSecondary">
            Not found
          </Text>
        </Box>
      )}
    </Dropdown>
  );
};
export default MultipleEmployerSelector;
